// src/services/albumService.js
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  setDoc,
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  increment,
  arrayUnion,
  arrayRemove,
  writeBatch ,
  serverTimestamp 
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { db, storage } from '../config/firebase';

class AlbumService {
  // Get all galleries for a book (using pb-books collection)
  async getBookGalleries(bookId) {
    try {
      const galleriesRef = collection(db, 'pb-books', bookId, 'galleries');
      const q = query(galleriesRef, orderBy('order', 'asc'));
      const snapshot = await getDocs(q);
      
      const galleries = await Promise.all(
        snapshot.docs.map(async (docSnap) => {
          const galleryData = docSnap.data();
          const mediaItems = await this.getGalleryMedia(bookId, docSnap.id);
          
          return {
            id: docSnap.id,
            ...galleryData,
            mediaItems
          };
        })
      );

      return galleries;
    } catch (error) {
      console.error('Error fetching galleries:', error);
      throw error;
    }
  }

  // Get media items for a specific gallery
  async getGalleryMedia(bookId, galleryId) {
    try {
      const mediaPath = `pb-books/${bookId}/galleries/${galleryId}/media`;
      
      const mediaRef = collection(db, 'pb-books', bookId, 'galleries', galleryId, 'media');
      
      // Order by the order field, then by uploadedAt as fallback
      const q = query(mediaRef, orderBy('order', 'asc'), orderBy('uploadedAt', 'asc'));
      const snapshot = await getDocs(q);
      
      const mediaItems = snapshot.docs.map(doc => ({
        ...doc.data(),        
        id: doc.id,           
        docId: doc.id,        
        mediaId: doc.data().id, 
        galleryId
      }));
      
      return mediaItems;
    } catch (error) {
      console.error(`❌ Error fetching gallery media for ${galleryId}:`, error);
      
      // If ordering fails (missing index), fallback to unordered query
      if (error.code === 'failed-precondition' || error.message.includes('index')) {
        try {
          const mediaRef = collection(db, 'pb-books', bookId, 'galleries', galleryId, 'media');
          const snapshot = await getDocs(mediaRef);
          
          const mediaItems = snapshot.docs.map(doc => ({
            ...doc.data(),        
            id: doc.id,           
            docId: doc.id,        
            mediaId: doc.data().id, 
            galleryId
          }));
          
          // Sort in JavaScript by order field if available, otherwise by uploadedAt
          mediaItems.sort((a, b) => {
            if (a.order !== undefined && b.order !== undefined) {
              return a.order - b.order;
            }
            if (a.uploadedAt && b.uploadedAt) {
              return a.uploadedAt.seconds - b.uploadedAt.seconds;
            }
            return 0;
          });
          
          return mediaItems;
        } catch (fallbackError) {
          console.error('❌ Fallback query also failed:', fallbackError);
          return [];
        }
      }
      
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      return [];
    }
  }

  async updateGalleryLayout(bookId, galleryId, layoutConfig) {
    try {
      const galleryRef = doc(db, 'pb-books', bookId, 'galleries', galleryId);
      await updateDoc(galleryRef, {
        layoutConfig: {
          ...layoutConfig,
          lastUpdated: serverTimestamp()
        },
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating gallery layout:', error);
      throw error;
    }
  }

  async createGallery(bookId, galleryData) {
    try {
      const galleriesRef = collection(db, 'pb-books', bookId, 'galleries');
      const docRef = await addDoc(galleriesRef, {
        ...galleryData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Update book gallery count
      const bookRef = doc(db, 'pb-books', bookId);
      await updateDoc(bookRef, {
        galleryCount: increment(1),
        updatedAt: serverTimestamp()
      });

      return docRef.id;
    } catch (error) {
      console.error('Error creating gallery:', error);
      throw error;
    }
  }

  async updateGallery(bookId, galleryId, updateData) {
    try {
      const galleryRef = doc(db, 'pb-books', bookId, 'galleries', galleryId);
      await updateDoc(galleryRef, {
        ...updateData,
        updatedAt: serverTimestamp()
      });
      
      return { id: galleryId, ...updateData };
    } catch (error) {
      console.error('Error updating gallery:', error);
      throw error;
    }
  }

  async uploadMediaFiles(bookId, galleryId, files, onProgress) {
    console.log('🚀 Starting uploadMediaFiles:', { bookId, galleryId, fileCount: files.length });
    
    const uploadPromises = files.map(async (file, index) => {
      let storageUrl = null;
      let mediaDocId = null;
      
      try {
        console.log(`📁 Processing file ${index + 1}/${files.length}: ${file.name}`);
        
        // 1. Storage upload (this is working)
        const mediaId = `${Date.now()}_${index}`;
        const fileExtension = file.name.split('.').pop();
        const fileName = `${mediaId}.${fileExtension}`;
        const storageRef = ref(storage, `albums/${bookId}/${galleryId}/${fileName}`);
        
        console.log(`⬆️ Step 1: Uploading to Storage...`);
        try {
          const snapshot = await uploadBytes(storageRef, file);
          console.log(`✅ Step 1 SUCCESS: File uploaded to Storage`);
          
          console.log(`🔗 Step 2: Getting download URL...`);
          storageUrl = await getDownloadURL(snapshot.ref);
          console.log(`✅ Step 2 SUCCESS: Download URL obtained: ${storageUrl.substring(0, 50)}...`);
          
        } catch (storageError) {
          console.error(`❌ Step 1-2 FAILED: Storage/URL error:`, storageError);
          throw new Error(`Storage upload failed: ${storageError.message}`);
        }
        
        // 2. Create media document in Firestore
        const mediaData = {
          type: file.type.startsWith('image/') ? 'image' : 'video',
          url: storageUrl,
          fileName,
          originalName: file.name,
          size: file.size,
          mimeType: file.type,
          uploadedAt: serverTimestamp(),
          order: index,
          mediaId: mediaId,
          interactions: {
            views: 0,
            likes: 0,
            comments: 0,
            isLiked: false,
            likedBy: []
          }
        };

        console.log(`📝 Step 3: Creating Firestore media document...`);
        try {
          const mediaRef = collection(db, 'pb-books', bookId, 'galleries', galleryId, 'media');
          const docRef = await addDoc(mediaRef, mediaData);
          mediaDocId = docRef.id;
          console.log(`✅ Step 3 SUCCESS: Media document created with ID: ${mediaDocId}`);
        } catch (firestoreError) {
          console.error(`❌ Step 3 FAILED: Firestore media creation error:`, firestoreError);
          console.error('Firestore error code:', firestoreError.code);
          console.error('Firestore error message:', firestoreError.message);
          throw new Error(`Media document creation failed: ${firestoreError.message}`);
        }

        // 3. Update gallery count
        console.log(`📊 Step 4: Updating gallery media count...`);
        try {
          const galleryRef = doc(db, 'pb-books', bookId, 'galleries', galleryId);
          await updateDoc(galleryRef, {
            mediaCount: increment(1),
            updatedAt: serverTimestamp()
          });
          console.log(`✅ Step 4 SUCCESS: Gallery count updated`);
        } catch (galleryError) {
          console.error(`⚠️ Step 4 FAILED: Gallery update error (non-critical):`, galleryError);
          // Don't fail the whole upload for this
        }

        // 4. Update book count
        console.log(`📚 Step 5: Updating book media count...`);
        try {
          const bookRef = doc(db, 'pb-books', bookId);
          await updateDoc(bookRef, {
            mediaCount: increment(1),
            updatedAt: serverTimestamp()
          });
          console.log(`✅ Step 5 SUCCESS: Book count updated`);
        } catch (bookError) {
          console.error(`⚠️ Step 5 FAILED: Book update error (non-critical):`, bookError);
          // Don't fail the whole upload for this
        }

        if (onProgress) {
          onProgress(index + 1, files.length);
        }

        console.log(`🎉 COMPLETE SUCCESS for: ${file.name}`);
        return { success: true, mediaId, url: storageUrl, docId: mediaDocId };
        
      } catch (error) {
        console.error(`❌ UPLOAD FAILED for ${file.name}:`, error);
        
        // If we got a storage URL but Firestore failed, note that
        if (storageUrl && !mediaDocId) {
          console.log(`⚠️ File uploaded to Storage but Firestore failed. URL: ${storageUrl}`);
          return { 
            success: false, 
            error: error.message, 
            fileName: file.name,
            storageUrl, // Include this so we know file exists
            partialSuccess: 'storage_only'
          };
        }
        
        return { success: false, error: error.message, fileName: file.name };
      }
    });

    const results = await Promise.all(uploadPromises);
    console.log('📋 All upload results:', results);
    
    // Log summary
    const successful = results.filter(r => r.success);
    const failed = results.filter(r => !r.success);
    const partialSuccess = results.filter(r => r.partialSuccess);
    
    console.log(`📊 UPLOAD SUMMARY: ${successful.length} success, ${failed.length} failed, ${partialSuccess.length} partial`);
    
    return results;
  }

  async uploadImage(file, path) {
    try {
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name}`;
      const storageRef = ref(storage, `${path}/${fileName}`);
      
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return downloadURL;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw new Error('Failed to upload image');
    }
  }

  async uploadVideo(file, path) {
    try {
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name}`;
      const storageRef = ref(storage, `${path}/${fileName}`);
      
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return {
        type: 'video',
        url: downloadURL,
        fileName,
        originalName: file.name,
        size: file.size,
        mimeType: file.type
      };
    } catch (error) {
      console.error('Error uploading video:', error);
      throw new Error('Failed to upload video');
    }
  }

  // Update book-level analytics
  async updateBookAnalytics(bookId, interactionType) {
    try {
      const bookRef = doc(db, 'pb-books', bookId);
      const analyticsUpdate = {};

      switch (interactionType) {
        case 'view':
          analyticsUpdate['analytics.views'] = increment(1);
          break;
        case 'like':
          analyticsUpdate['analytics.interactions'] = increment(1);
          break;
        case 'comment':
          analyticsUpdate['analytics.interactions'] = increment(1);
          break;
      }

      analyticsUpdate.lastActivityAt = serverTimestamp();

      await updateDoc(bookRef, analyticsUpdate);
    } catch (error) {
      console.error('Error updating book analytics:', error);
    }
  }

  async uploadFile(bookId, file, folder = 'media') {
    try {
      const fileExtension = file.name.split('.').pop();
      const fileName = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}.${fileExtension}`;
      
      // Use the folder parameter correctly here
      const storageRef = ref(storage, `albums/${bookId}/${folder}/${fileName}`);
      
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return {
        url: downloadURL,
        fileName,
        originalName: file.name,
        size: file.size,
        mimeType: file.type
      };
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  }

  async updateMediaMetadata(bookId, galleryId, mediaId, metadata) {
    try {
      const mediaRef = doc(db, 'pb-books', bookId, 'galleries', galleryId, 'media', mediaId);
      
      // Flatten metadata to root level - ensures consistency
      const flattenedMetadata = {};
      Object.entries(metadata).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          flattenedMetadata[key] = value;
        }
      });

      await updateDoc(mediaRef, {
        ...flattenedMetadata,
        updatedAt: serverTimestamp()
      });

      return true;
    } catch (error) {
      console.error('Error updating media metadata:', error);
      throw error;
    }
  }

  async addYoutubeVideo(bookId, galleryId, youtubeData) {
    console.log('🎥 Starting addYoutubeVideo:', { bookId, galleryId, youtubeData });
    
    try {
      // 1. Get current media count for order
      const mediaRef = collection(db, 'pb-books', bookId, 'galleries', galleryId, 'media');
      const snapshot = await getDocs(mediaRef);
      const currentOrder = snapshot.size;

      // 2. Create YouTube media document
      const mediaData = {
        type: 'youtube',
        url: youtubeData.url,
        videoId: youtubeData.videoId,
        title: youtubeData.title || 'YouTube Video',
        description: youtubeData.description || '',
        thumbnailUrl: youtubeData.thumbnailUrl || `https://img.youtube.com/vi/${youtubeData.videoId}/maxresdefault.jpg`,
        
        // Optional metadata
        fileName: null,
        originalName: youtubeData.title || 'YouTube Video',
        size: 0,
        mimeType: 'video/youtube',
        
        // Standard fields
        uploadedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        order: currentOrder,
        visible: true,
        
        // Interactions
        interactions: {
          views: 0,
          likes: 0,
          comments: 0,
          isLiked: false,
          likedBy: []
        },
        
        // Additional metadata if provided
        ...(youtubeData.metadata && { metadata: youtubeData.metadata })
      };

      console.log('📝 Creating Firestore YouTube media document...');
      
      // 3. Add document to Firestore
      const docRef = await addDoc(mediaRef, mediaData);
      console.log(`✅ YouTube media document created with ID: ${docRef.id}`);

      // 4. Update gallery media count
      console.log('📊 Updating gallery media count...');
      const galleryRef = doc(db, 'pb-books', bookId, 'galleries', galleryId);
      await updateDoc(galleryRef, {
        mediaCount: increment(1),
        updatedAt: serverTimestamp()
      });
      console.log('✅ Gallery count updated');

      // 5. Update book media count
      console.log('📚 Updating book media count...');
      const bookRef = doc(db, 'pb-books', bookId);
      await updateDoc(bookRef, {
        mediaCount: increment(1),
        updatedAt: serverTimestamp()
      });
      console.log('✅ Book count updated');

      console.log('🎉 YouTube video added successfully');
      return { 
        success: true, 
        mediaId: docRef.id, 
        url: youtubeData.url,
        videoId: youtubeData.videoId 
      };
      
    } catch (error) {
      console.error('❌ Error adding YouTube video:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      throw new Error(`Failed to add YouTube video: ${error.message}`);
    }
  }

  // Batch add multiple YouTube videos
  async addMultipleYoutubeVideos(bookId, galleryId, youtubeVideos, onProgress) {
    console.log('🎥 Starting batch YouTube video upload:', { 
      bookId, 
      galleryId, 
      videoCount: youtubeVideos.length 
    });
    
    const results = [];
    
    for (let i = 0; i < youtubeVideos.length; i++) {
      try {
        const result = await this.addYoutubeVideo(bookId, galleryId, youtubeVideos[i]);
        results.push({ success: true, index: i, ...result });
        
        if (onProgress) {
          onProgress(i + 1, youtubeVideos.length);
        }
      } catch (error) {
        console.error(`Failed to add YouTube video ${i + 1}:`, error);
        results.push({ 
          success: false, 
          index: i, 
          error: error.message,
          url: youtubeVideos[i].url 
        });
      }
    }
    
    return results;
  }

  async getMediaExpressions(bookId, galleryId, mediaId) {
    try {
      const expressionsRef = collection(db, 'pb-books', bookId, 'galleries', galleryId, 'media', mediaId, 'expressions');
      const snapshot = await getDocs(expressionsRef);
      
      const expressions = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      return { expressions };
    } catch (error) {
      console.error('Error getting media expressions:', error);
      throw error;
    }
  }

  async addMediaComment(bookId, galleryId, mediaId, commentData) {
    try {
      const expressionsRef = collection(db, 'pb-books', bookId, 'galleries', galleryId, 'media', mediaId, 'expressions');
      
      await addDoc(expressionsRef, {
        ...commentData,
        action: 'comment',
        createdAt: serverTimestamp()
      });

      // Update media comment count - FIXED to match actual structure
      const mediaRef = doc(db, 'pb-books', bookId, 'galleries', galleryId, 'media', mediaId);
      await updateDoc(mediaRef, {
        'interactions.comments': increment(1),           // ← Fixed: was 'expressions.commentCount'
        'interactions.lastCommentAt': serverTimestamp()  // ← Fixed: was 'expressions.lastCommentAt'
      });

      return true;
    } catch (error) {
      console.error('Error adding comment:', error);
      throw error;
    }
  }

  // Toggle expression - FIXED FIELD NAMES
  async toggleMediaExpression(bookId, galleryId, mediaId, expressionType, userId, userInfo) {
    try {
      const expressionsRef = collection(db, 'pb-books', bookId, 'galleries', galleryId, 'media', mediaId, 'expressions');
      
      // Check if user already has this expression
      const existingQuery = query(
        expressionsRef, 
        where('userId', '==', userId),
        where('type', '==', expressionType),
        where('action', '==', 'expression')
      );
      const existingSnapshot = await getDocs(existingQuery);

      const mediaRef = doc(db, 'pb-books', bookId, 'galleries', galleryId, 'media', mediaId);

      if (existingSnapshot.empty) {
        // Add expression
        await addDoc(expressionsRef, {
          type: expressionType,
          action: 'expression',
          userId,
          userName: userInfo.userName,
          userAvatar: userInfo.userAvatar || null,
          createdAt: serverTimestamp()
        });

        // Update media expressions count - FIXED field names
       await updateDoc(mediaRef, {
          [`interactions.expressionCounts.${expressionType}`]: increment(1),
          'interactions.lastExpressionAt': serverTimestamp()
        });

        return { added: true };
      } else {
        // Remove expression
        const expressionDoc = existingSnapshot.docs[0];
        await deleteDoc(expressionDoc.ref);

        // Update media expressions count - FIXED field names
        await updateDoc(mediaRef, {
          [`interactions.${expressionType}Count`]: increment(-1)  // ← Fixed field structure
        });

        return { added: false };
      }
    } catch (error) {
      console.error('Error toggling expression:', error);
      throw error;
    }
  }

  async updateMediaInteraction(bookId, galleryId, mediaId, interactionType, userId) {
    try {
      const mediaRef = doc(db, 'pb-books', bookId, 'galleries', galleryId, 'media', mediaId);
      const mediaDoc = await getDoc(mediaRef);
      
      if (!mediaDoc.exists()) {
        throw new Error('Media not found');
      }

      const currentData = mediaDoc.data();
      const interactions = currentData.interactions || {};

      switch (interactionType) {
        case 'view':
          await updateDoc(mediaRef, {
            'interactions.views': increment(1),                    // ✅ Correct
            lastViewedAt: serverTimestamp()                        // ✅ Correct
          });
          break;

        case 'like':
          const isLiked = interactions.likedBy?.includes(userId) || false;
          
          if (isLiked) {
            // Unlike
            await updateDoc(mediaRef, {
              'interactions.likes': increment(-1),                 // ✅ Correct
              'interactions.likedBy': arrayRemove(userId)          // ✅ Correct
            });
          } else {
            // Like
            await updateDoc(mediaRef, {
              'interactions.likes': increment(1),                  // ✅ Correct
              'interactions.likedBy': arrayUnion(userId)           // ✅ Correct
            });
          }
          
          return !isLiked; // Return new like state
          
        case 'comment':
          await updateDoc(mediaRef, {
            'interactions.comments': increment(1),                 // ✅ Correct
            'interactions.lastCommentAt': serverTimestamp()        // ← Fixed field name
          });
          break;
      }

      return true;
    } catch (error) {
      console.error('Error updating media interaction:', error);
      throw error;
    }
  }

  async toggleMediaVisibility(bookId, galleryId, mediaId, visible) {
    try {
      const mediaRef = doc(db, 'pb-books', bookId, 'galleries', galleryId, 'media', mediaId);
      
      await updateDoc(mediaRef, {
        visible,
        updatedAt: serverTimestamp()
      });

      return true;
    } catch (error) {
      console.error('Error toggling media visibility:', error);
      throw error;
    }
  }

  // Optional: Bulk visibility toggle for multiple items
  async bulkToggleMediaVisibility(bookId, galleryId, mediaIds, visible) {
    try {
      const updatePromises = mediaIds.map(mediaId => {
        const mediaRef = doc(db, 'pb-books', bookId, 'galleries', galleryId, 'media', mediaId);
        return updateDoc(mediaRef, {
          visible,
          updatedAt: serverTimestamp()
        });
      });

      await Promise.all(updatePromises);
      return true;
    } catch (error) {
      console.error('Error bulk toggling visibility:', error);
      throw error;
    }
  }

  async deleteMedia(bookId, galleryId, mediaId) {
    try {
      // Get the media document to retrieve the file URL
      const mediaRef = doc(db, 'pb-books', bookId, 'galleries', galleryId, 'media', mediaId);
      const mediaDoc = await getDoc(mediaRef);
      
      if (mediaDoc.exists()) {
        const mediaData = mediaDoc.data();
        
        // Delete the file from Storage if URL exists
        if (mediaData.url) {
          try {
            const fileRef = ref(storage, mediaData.url);
            await deleteObject(fileRef);
            console.log('File deleted from storage');
          } catch (storageError) {
            console.warn('Could not delete file from storage:', storageError);
            // Continue with Firestore deletion even if storage deletion fails
          }
        }
        
        // Delete thumbnail if exists
        if (mediaData.thumbnail) {
          try {
            const thumbnailRef = ref(storage, mediaData.thumbnail);
            await deleteObject(thumbnailRef);
          } catch (thumbError) {
            console.warn('Could not delete thumbnail:', thumbError);
          }
        }
      }
      
      // Delete the Firestore document
      await deleteDoc(mediaRef);
      
      // Update gallery media count
      const galleryRef = doc(db, 'pb-books', bookId, 'galleries', galleryId);
      await updateDoc(galleryRef, {
        mediaCount: increment(-1),
        updatedAt: serverTimestamp()
      });
      
      return true;
    } catch (error) {
      console.error('Error deleting media:', error);
      throw error;
    }
  }

  async deleteFile(fileUrl) {
    try {
      const fileRef = ref(storage, fileUrl);
      await deleteObject(fileRef);
      return true;
    } catch (error) {
      console.error('Error deleting file:', error);
      throw error;
    }
  }

  async uploadAlbumVoice(bookId, audioBlob, metadata) {
    try {
      const audioFile = new File([audioBlob], `album-voice-${Date.now()}.webm`, {
        type: 'audio/webm'
      });

      const uploadResult = await this.uploadFile(
        bookId,
        audioFile,
        'album/voice'
      );

      const bookRef = doc(db, 'pb-books', bookId);
      await updateDoc(bookRef, {
        albumVoice: {
          url: uploadResult.url,
          transcript: metadata.transcript || '',
          speaker: metadata.speaker,
          duration: metadata.duration,
          language: metadata.language || 'en',
          createdAt: serverTimestamp(),
          isActive: true
        },
        updatedAt: serverTimestamp()
      });

      return uploadResult;
    } catch (error) {
      console.error('Error uploading album voice:', error);
      throw error;
    }
  }

  // GALLERY-LEVEL VOICE
  async uploadGalleryVoice(bookId, galleryId, audioBlob, metadata) {
    try {
      const audioFile = new File([audioBlob], `gallery-voice-${Date.now()}.webm`, {
        type: 'audio/webm'
      });

      const uploadResult = await this.uploadFile(
        bookId,
        audioFile,
        `gallery/${galleryId}/voice`
      );

      const galleryRef = doc(db, 'pb-books', bookId, 'galleries', galleryId);
      await updateDoc(galleryRef, {
        galleryVoice: {
          url: uploadResult.url,
          transcript: metadata.transcript || '',
          speaker: metadata.speaker,
          duration: metadata.duration,
          displayMode: metadata.displayMode || 'banner',
          autoPlay: metadata.autoPlay || false,
          createdAt: serverTimestamp()
        },
        updatedAt: serverTimestamp()
      });

      return uploadResult;
    } catch (error) {
      console.error('Error uploading gallery voice:', error);
      throw error;
    }
  }

  // GALLERY VOICE SEGMENTS
  async addVoiceSegment(bookId, galleryId, audioBlob, segmentData) {
    try {
      const audioFile = new File([audioBlob], `segment-${Date.now()}.webm`, {
        type: 'audio/webm'
      });

      const uploadResult = await this.uploadFile(
        bookId,
        audioFile,
        `gallery/${galleryId}/segments`
      );

      // 🔧 FIX: Use Date object instead of serverTimestamp()
      const segment = {
        id: `segment-${Date.now()}`,
        title: segmentData.title,
        voiceUrl: uploadResult.url,
        transcript: segmentData.transcript || '',
        mediaRange: segmentData.mediaRange,
        speaker: segmentData.speaker,
        duration: segmentData.duration,
        createdAt: new Date().toISOString() // ✅ Use ISO string
      };

      const galleryRef = doc(db, 'pb-books', bookId, 'galleries', galleryId);
      await updateDoc(galleryRef, {
        voiceSegments: arrayUnion(segment),
        updatedAt: serverTimestamp() // ✅ This is fine at the document level
      });

      return segment;
    } catch (error) {
      console.error('Error adding voice segment:', error);
      throw error;
    }
  }

  // MEDIA-LEVEL VOICE NOTES
  async addMediaVoiceNote(bookId, galleryId, mediaId, audioBlob, noteData) {
    try {
      const audioFile = new File([audioBlob], `voice-note-${Date.now()}.webm`, {
        type: 'audio/webm'
      });

      const uploadResult = await this.uploadFile(
        bookId,
        audioFile,
        `gallery/${galleryId}/media/${mediaId}/voice-notes`
      );

      // 🔧 FIX: Use Date object instead of serverTimestamp() for arrayUnion
      const voiceNote = {
        id: `voice-${Date.now()}`,
        url: uploadResult.url,
        transcript: noteData.transcript || '',
        speaker: noteData.speaker,
        duration: noteData.duration,
        language: noteData.language || 'en',
        isPinned: noteData.isPinned || false,
        reactions: {
          likes: 0,
          replies: []
        },
        createdAt: new Date().toISOString(), // ✅ Use ISO string instead of serverTimestamp()
        createdBy: noteData.userId || null
      };

      const mediaRef = doc(db, 'pb-books', bookId, 'galleries', galleryId, 'media', mediaId);
      await updateDoc(mediaRef, {
        voiceNotes: arrayUnion(voiceNote),
        'voiceContext.count': increment(1),
        ...(noteData.isPinned && { 'voiceContext.primary': voiceNote.id }),
        updatedAt: serverTimestamp() // ✅ This is fine at the document level
      });

      return voiceNote;
    } catch (error) {
      console.error('Error adding media voice note:', error);
      throw error;
    }
  }

  // PIN/UNPIN VOICE NOTE
  async togglePinVoiceNote(bookId, galleryId, mediaId, voiceNoteId) {
    try {
      const mediaRef = doc(db, 'pb-books', bookId, 'galleries', galleryId, 'media', mediaId);
      await updateDoc(mediaRef, {
        'voiceContext.primary': voiceNoteId,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error pinning voice note:', error);
      throw error;
    }
  }

  async getAlbumContributors(bookId) {
    try {
      const contributorsRef = collection(db, 'pb-books', bookId, 'contributors');
      const q = query(contributorsRef, orderBy('approvedAt', 'desc'));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting album contributors:', error);
      throw error;
    }
  }

  async addAlbumContributor(bookId, contributorData) {
    try {
      const contributorsRef = collection(db, 'pb-books', bookId, 'contributors');
      const docRef = await addDoc(contributorsRef, {
        ...contributorData,
        approvedAt: serverTimestamp(),
        createdAt: serverTimestamp()
      });
      
      return { id: docRef.id, ...contributorData };
    } catch (error) {
      console.error('Error adding album contributor:', error);
      throw error;
    }
  }

  async removeAlbumContributor(bookId, contributorId) {
    try {
      const contributorRef = doc(db, 'pb-books', bookId, 'contributors', contributorId);
      await deleteDoc(contributorRef);
    } catch (error) {
      console.error('Error removing album contributor:', error);
      throw error;
    }
  }

  async isApprovedContributor(bookId, userId) {
    try {
      const contributorsRef = collection(db, 'pb-books', bookId, 'contributors');
      const q = query(contributorsRef, where('userId', '==', userId), limit(1));
      const snapshot = await getDocs(q);
      
      return !snapshot.empty;
    } catch (error) {
      console.error('Error checking contributor status:', error);
      return false;
    }
  }

  async uploadUserMedia(bookId, galleryId, file, userId, userEmail, userName, isApproved) {
    try {
      // Upload to storage
      const fileExt = file.name.split('.').pop();
      const mediaId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const fileName = `${mediaId}.${fileExt}`;
      const storagePath = `albums/${bookId}/${galleryId}/${fileName}`;
      const storageRef = ref(storage, storagePath);
      
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      // Create media document
      const mediaData = {
        type: file.type.startsWith('video/') ? 'video' : 'image',
        url: downloadURL,
        fileName: file.name,
        originalName: file.name,
        size: file.size,
        mimeType: file.type,
        uploadedBy: userId,
        uploaderEmail: userEmail,
        uploaderName: userName,
        status: isApproved ? 'approved' : 'pending',
        visible: isApproved,
        canEditMetadata: isApproved,
        uploadedAt: serverTimestamp(),
        order: 999,
        interactions: {
          views: 0,
          likes: 0,
          comments: 0,
          likedBy: []
        }
      };
      
      const mediaRef = collection(db, 'pb-books', bookId, 'galleries', galleryId, 'media');
      const docRef = await addDoc(mediaRef, mediaData);
      
      // Update gallery count
      const galleryRef = doc(db, 'pb-books', bookId, 'galleries', galleryId);
      await updateDoc(galleryRef, {
        mediaCount: increment(1),
        updatedAt: serverTimestamp()
      });
      
      return { id: docRef.id, url: downloadURL };
    } catch (error) {
      console.error('Error uploading user media:', error);
      throw error;
    }
  }
  // ADD these methods to the AlbumService class

  async getPendingMedia(bookId) {
    try {
      const galleries = await this.getBookGalleries(bookId);
      const pendingMedia = [];
      
      galleries.forEach(gallery => {
        const galleryPendingMedia = (gallery.mediaItems || []).filter(
          item => item.status === 'pending' && item.uploadedBy
        );
        
        galleryPendingMedia.forEach(item => {
          pendingMedia.push({
            ...item,
            galleryId: gallery.id,
            galleryName: gallery.name
          });
        });
      });
      
      return pendingMedia;
    } catch (error) {
      console.error('Error getting pending media:', error);
      throw error;
    }
  }

  async approveMedia(bookId, galleryId, mediaId, approverUserId) {
    try {
      const mediaRef = doc(db, 'pb-books', bookId, 'galleries', galleryId, 'media', mediaId);
      
      await updateDoc(mediaRef, {
        status: 'approved',
        visible: true,
        canEditMetadata: true,
        moderatedBy: approverUserId,
        moderatedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return true;
    } catch (error) {
      console.error('Error approving media:', error);
      throw error;
    }
  }

  async rejectMedia(bookId, galleryId, mediaId, approverUserId, reason = '') {
    try {
      const mediaRef = doc(db, 'pb-books', bookId, 'galleries', galleryId, 'media', mediaId);
      
      await updateDoc(mediaRef, {
        status: 'rejected',
        visible: false,
        canEditMetadata: false,
        moderatedBy: approverUserId,
        moderatedAt: serverTimestamp(),
        moderationNote: reason,
        updatedAt: serverTimestamp()
      });
      
      return true;
    } catch (error) {
      console.error('Error rejecting media:', error);
      throw error;
    }
  }

  async updateGalleryVoicePermissions(bookId, galleryId, permissions) {
    try {
      const galleryRef = doc(db, 'pb-books', bookId, 'galleries', galleryId);
      await updateDoc(galleryRef, {
        'voicePermissions': permissions,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating gallery voice permissions:', error);
      throw error;
    }
  }

  async reorderGalleryMedia(bookId, galleryId, mediaItems) {
    try {
      const batch = writeBatch(db);
      
      // Update each media item with its new order
      mediaItems.forEach((item, index) => {
        const mediaRef = doc(db, 'pb-books', bookId, 'galleries', galleryId, 'media', item.id);
        batch.update(mediaRef, { order: index });
      });
      
      await batch.commit();
      console.log('✅ Media order updated successfully');
      return { success: true };
    } catch (error) {
      console.error('❌ Error reordering gallery media:', error);
      throw error;
    }
  }

  async deleteMediaVoiceNote(bookId, galleryId, mediaId, voiceNoteId) {
    try {
      const mediaRef = doc(db, 'pb-books', bookId, 'galleries', galleryId, 'media', mediaId);
      const mediaDoc = await getDoc(mediaRef);
      
      if (!mediaDoc.exists()) {
        throw new Error('Media not found');
      }
      
      const voiceNotes = mediaDoc.data().voiceNotes || [];
      const noteToDelete = voiceNotes.find(note => note.id === voiceNoteId);
      
      if (!noteToDelete) {
        throw new Error('Voice note not found');
      }
      
      // Delete audio file from storage
      if (noteToDelete.url) {
        await this.deleteFile(noteToDelete.url);
      }
      
      // Remove from array
      const updatedNotes = voiceNotes.filter(note => note.id !== voiceNoteId);
      
      await updateDoc(mediaRef, {
        voiceNotes: updatedNotes,
        'voiceContext.count': increment(-1),
        updatedAt: serverTimestamp()
      });
      
      return true;
    } catch (error) {
      console.error('Error deleting media voice note:', error);
      throw error;
    }
  }

  async reorderGalleries(bookId, galleries) {
    try {
      const batch = [];
      for (const gallery of galleries) {
        const galleryRef = doc(db, 'pb-books', bookId, 'galleries', gallery.id);
        batch.push(updateDoc(galleryRef, { order: gallery.order }));
      }
      await Promise.all(batch);
    } catch (error) {
      console.error('Error reordering galleries:', error);
      throw error;
    }
  }

  async recordMediaTransaction(transactionData) {
    try {
      const {
        albumId,
        galleryId,
        mediaItemId,
        itemName,
        itemPrice,
        itemDescription,
        transactionType,
        method,
        userEmail,
        userName,
        userId,
        startDate,
        endDate,
        quantity,
        area
      } = transactionData;

      const transactionRef = collection(db, 'pb-transactions');

      await addDoc(transactionRef, {
        albumId,
        galleryId,
        mediaItemId,
        itemName,
        itemPrice: itemPrice || null,
        itemDescription: itemDescription || null,
        transactionType,
        method,
        userEmail: userEmail || null,
        userName: userName || null,
        userId: userId || null,
        startDate: startDate || null,
        endDate: endDate || null,
        quantity: quantity || 1,
        area: area || null,
        timestamp: serverTimestamp(),
        createdAt: new Date().toISOString()
      });

      return true;
    } catch (error) {
      console.error('Error recording transaction:', error);
      throw error;
    }
  }

  async moveMediaBetweenGalleries(bookId, sourceGalleryId, targetGalleryId, mediaId) {
    try {
      const sourceMediaRef = doc(db, 'pb-books', bookId, 'galleries', sourceGalleryId, 'media', mediaId);
      const mediaSnap = await getDoc(sourceMediaRef);
      
      if (!mediaSnap.exists()) {
        throw new Error('Media not found');
      }
      
      const mediaData = mediaSnap.data();
      
      const targetMediaRef = collection(db, 'pb-books', bookId, 'galleries', targetGalleryId, 'media');
      const targetSnapshot = await getDocs(targetMediaRef);
      const newOrder = targetSnapshot.size;
      
      const newMediaRef = doc(db, 'pb-books', bookId, 'galleries', targetGalleryId, 'media', mediaId);
      
      await setDoc(newMediaRef, {
        ...mediaData,
        order: newOrder,
        movedAt: serverTimestamp(),
        previousGalleryId: sourceGalleryId
      });
      
      await deleteDoc(sourceMediaRef);
      
      await updateDoc(doc(db, 'pb-books', bookId, 'galleries', sourceGalleryId), {
        updatedAt: serverTimestamp()
      });
      
      await updateDoc(doc(db, 'pb-books', bookId, 'galleries', targetGalleryId), {
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error moving media:', error);
      throw error;
    }
  }
}

export const albumService = new AlbumService();